import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import type {
    CancelInfoParams,
    CancelInfoResponse,
    CancelReasonsParams,
    CancelReasonsResponse,
    CancelTripParams,
    CancelTripResponse
} from '@/src/types/cancellation'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { toQueryString } from '@/src/utils/generalUtils/toQueryString'
import { buildFlyinQuerySuffix, getFetchControlHeader, getTripApiBase } from '@/src/utils/rtkQuery/tripApiBase'

const SOURCE_TYPE = 'ACCOUNT'

// cancelInfo query string: always sourceType, plus abIds/cancelReasonCode on step 1.
const buildCancelInfoQuery = (chnl?: string, abIds?: string, cancelReasonCode?: string) => {
    const flyinSuffix = buildFlyinQuerySuffix(chnl)
    const params = toQueryString({
        sourceType: SOURCE_TYPE,
        abIds: abIds || undefined,
        cancelReasonCode: cancelReasonCode || undefined
    })
    if (!flyinSuffix) return `?${params}`
    return `${flyinSuffix}&${params}`
}

export const cancellationApi = createApi({
    reducerPath: 'cancellationApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: headers => {
            setCommonHeaders(headers)
            // Report the cancellation flow's source as the account app rather than
            // the device type (Desktop/Mobile) that setCommonHeaders sets by default.
            headers.set('X_CT_SOURCETYPE', SOURCE_TYPE)
            return headers
        }
    }),
    tagTypes: ['Cancellation'],
    endpoints: builder => ({
        // Step 0 load AND step 1 refund compute — same endpoint, called twice.
        getCancelInfo: builder.query<CancelInfoResponse, CancelInfoParams>({
            query: ({ tripId, chnl, account, abIds, cancelReasonCode }) => ({
                url: `${getTripApiBase(chnl)}/cancelInfo/v2/${tripId}${buildCancelInfoQuery(chnl, abIds, cancelReasonCode)}`,
                headers: getFetchControlHeader({ chnl, account })
            })
        }),

        getCancelReasons: builder.query<CancelReasonsResponse, CancelReasonsParams>({
            query: ({ tripId, chnl, account, abIds }) => ({
                url: `${getTripApiBase(chnl)}/getCancelReasons/v2/${tripId}?${toQueryString({
                    abIds,
                    sourceType: SOURCE_TYPE
                })}`,
                headers: getFetchControlHeader({ chnl, account })
            })
        }),

        cancelTrip: builder.mutation<CancelTripResponse, CancelTripParams>({
            query: ({ tripId, abIds, cancelReasonCode, alternateRefund, nonGuaranteed, chnl, account }) => ({
                url: `${getTripApiBase(chnl)}/cancel/v2/${tripId}`,
                method: 'POST',
                body: {
                    abIds,
                    cancelReasonCode,
                    alternateRefund,
                    nonGuaranteed
                },
                headers: getFetchControlHeader({ chnl, account })
            })
        })
    })
})

export const {
    useLazyGetCancelInfoQuery,
    useLazyGetCancelReasonsQuery,
    useCancelTripMutation
} = cancellationApi

export default cancellationApi
